import { Box, LinearProgress, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomTextField,
  CustomSelect,
  CustomAutoComplete,
} from '../../../../common/components';
import { MenuItem } from '@mui/material';
import { LOT_STATUS_OPTIONS, PERSON_NAME_OPTIONS } from '../constants/lotsConstants';
import { normalizeLotStatusValue } from '../utils/lotsUtils';

const LotDialog = ({
  open,
  onClose,
  editMode,
  selectedLot,
  onSave,
  contracts = [],
  constructionTypes = [],
  allLots = [],
  project,
  contract,
  subscribers = [],
  canEditLots = true,
}) => {
  const [tab, setTab] = useState(0);
  const [formState, setFormState] = useState(selectedLot || {});
  const hasProject = Boolean(project?.uuid);
  const contractOptions = Array.isArray(contracts) ? contracts : [];
  const constructionTypeOptions = Array.isArray(constructionTypes) ? constructionTypes : [];
  const assignedContractIds = new Set(
    (Array.isArray(allLots) ? allLots : [])
      .map((lot) => lot?.uuid_contract)
      .filter(Boolean)
  );
  const currentLotId = selectedLot?.uuid;
  const currentContractId = selectedLot?.uuid_contract;
  const availableContracts = contractOptions.filter((option) => {
    if (!option?.uuid) return false;
    if (!assignedContractIds.has(option.uuid)) return true;
    if (currentContractId && option.uuid === currentContractId) return true;
    if (
      currentLotId &&
      (Array.isArray(allLots) ? allLots : []).some(
        (lot) => lot?.uuid === currentLotId && lot?.uuid_contract === option.uuid
      )
    ) {
      return true;
    }
    return false;
  });
  const hasTickets =
    Array.isArray(selectedLot?.lot_tickets) &&
    selectedLot.lot_tickets.length > 0 &&
    selectedLot.lot_tickets.some((ticket) => ticket && ticket.uuid);

  useEffect(() => {
    if (open) {
      setFormState({
        ...(selectedLot || {}),
        status: normalizeLotStatusValue(selectedLot?.status),
      });
      setTab(0);
    }
  }, [open, selectedLot]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const formatProjectStatus = (value) => {
    if (!value) return '-';
    const labelMap = {
      not_started: 'No iniciada',
      in_progress: 'En progreso',
      completed: 'Finalizada',
      cancelled: 'Cancelada',
    };
    return labelMap[value] || value;
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatMonthYear = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('es-AR', {
      month: 'long',
      year: 'numeric',
    });
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString('es-AR');
  };

  const handleSave = () => {
    onSave?.(formState);
  };

  const getContractLabel = (option) => {
    if (!option?.contract_number) return '';
    return `#${option.contract_number}${option.is_archived ? ' (Archivado)' : ''}`;
  };

  const getSubscriberLabel = (subscriber) => {
    if (!subscriber) return '';
    const lastName = subscriber.last_name || '';
    const firstName = subscriber.first_name || '';
    return `${lastName}${lastName && firstName ? ', ' : ''}${firstName}`.trim();
  };

  const getDistrictName = (ticket) => ticket?.district_name || '-';
  const getMaritalStatusName = (ticket) => ticket?.marital_status_name || '-';
  const getNationalityName = (ticket) => ticket?.nationality_name || '-';

  const getGenderLabel = (value) => {
    if (!value) return '-';
    const labelMap = {
      h: 'Masculino',
      m: 'Femenino',
      O: 'Otro',
    };
    return labelMap[value] || value;
  };

  return (
    <DialogContainer open={open} onClose={onClose} size="small" minHeight="80vh">
      <DialogTitle>
        {editMode
          ? `Detalle de lote ${
              selectedLot?.block_number
                ? `${selectedLot.block_number}-${selectedLot.lot_number}`
                : selectedLot?.lot_number
            }`
          : 'Agregar Lote'}
      </DialogTitle>

      <DialogBody>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
          <Tab label="Lote" />
          {editMode && <Tab label="Contrato" />}
          {editMode && <Tab label="Obra" disabled={!hasProject} />}
          {editMode && <Tab label="Boleto" disabled={!hasTickets} />}
        </Tabs>

        {tab === 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <CustomTextField
              label="Manzana"
              name="block_number"
              value={formState?.block_number || ''}
              onChange={handleInputChange}
            />
            <CustomTextField
              label="Número de lote"
              name="lot_number"
              value={formState?.lot_number || ''}
              onChange={handleInputChange}
            />
            <CustomTextField
              label="Catastro"
              name="cadastral_number"
              value={formState?.cadastral_number || ''}
              onChange={handleInputChange}
            />
            <CustomAutoComplete
              label="Tipo de construcción"
              name="uuid_construction_type"
              value={formState?.uuid_construction_type || ''}
              onChange={handleInputChange}
              options={constructionTypeOptions}
              optionValueKey="uuid"
              optionLabelKey="name"
              getOptionLabel={(option) => option?.name || ''}
              placeholder="Sin tipo de construcción"
            />
            <CustomTextField
              label="Superficie (m2)"
              name="surface_area"
              type="number"
              value={formState?.surface_area ?? ''}
              onChange={handleInputChange}
              inputProps={{ min: 0, step: '0.01' }}
            />
            <CustomTextField
              label="Fecha estimada de entrega"
              name="estimated_delivery_date"
              type="date"
              value={formState?.estimated_delivery_date || ''}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
            <CustomSelect
              label="Estado"
              name="status"
              value={formState?.status || ''}
              onChange={handleInputChange}
            >
              {LOT_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </CustomSelect>
            <CustomSelect
              label="Asignado a"
              name="person_name"
              value={formState?.person_name || ''}
              onChange={handleInputChange}
            >
              <MenuItem value="">Sin asignar</MenuItem>
              {PERSON_NAME_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </CustomSelect>
              <CustomAutoComplete
              label="Contrato"
              name="uuid_contract"
              value={formState?.uuid_contract || ''}
              onChange={handleInputChange}
              options={availableContracts}
              optionValueKey="uuid"
              optionLabelKey="contract_number"
              getOptionLabel={getContractLabel}
              placeholder="Sin contrato"
              renderOption={(props, option) => {
                const subscriberItems =
                  Array.isArray(option.subscribers) && option.subscribers.length > 0
                    ? option.subscribers.map(getSubscriberLabel).filter(Boolean)
                    : [];
                return (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      width: '100%',
                      textAlign: 'left',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        textAlign: 'left',
                        width: '100%',
                      }}
                    >
                      {getContractLabel(option)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5, textAlign: 'left', width: '100%' }}
                    >
                      Suscriptores
                    </Typography>
                    {subscriberItems.length > 0 ? (
                      <Box component="ul" sx={{ pl: 2, my: 0.5, width: '100%' }}>
                        {subscriberItems.map((subscriber, index) => (
                          <Typography
                            component="li"
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: 'left', width: '100%' }}
                            key={`${option.uuid}-subscriber-${index}`}
                          >
                            {subscriber}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: 'left', width: '100%' }}
                      >
                        Sin suscriptores
                      </Typography>
                    )}
                  </Box>
                );
              }}
            />
          </Box>
        )}

        {editMode && tab === 1 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                Contrato
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                {contract?.contract_number ? `#${contract.contract_number}` : 'Sin contrato'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                Suscriptores
              </Typography>
              {subscribers.length > 0 ? (
                subscribers.map((subscriber) => (
                  <Typography
                    key={subscriber.uuid || subscriber.dni}
                    variant="body1"
                    sx={{ fontSize: '1rem' }}
                  >
                    {subscriber.last_name}, {subscriber.first_name}
                  </Typography>
                ))
              ) : (
                <Typography variant="body1" sx={{ fontSize: '1rem' }} color="text.secondary">
                  Sin suscriptores asociados.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {editMode && tab === 2 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                Estado de la obra
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                {formatProjectStatus(project?.status)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                Subcontratista asignado
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                {project?.subcontractor_name || '-'}
              </Typography>
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                Progreso de obra
              </Typography>
              <Box sx={{ display: 'grid', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={Number(selectedLot?.project_progress_summary?.completion_percentage ?? 0)}
                />
                <Typography variant="body1" sx={{ fontSize: '0.95rem' }}>
                  {selectedLot?.project_progress_summary?.completion_percentage ?? 0}%
                </Typography>
              </Box>
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                Calidad de obra
              </Typography>
              <Box sx={{ display: 'grid', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={Number(selectedLot?.project_quality_summary?.completion_percentage ?? 0)}
                />
                <Typography variant="body1" sx={{ fontSize: '0.95rem' }}>
                  {selectedLot?.project_quality_summary?.completion_percentage ?? 0}%
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                Fecha de inicio de obra
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                {formatMonthYear(project?.start_date)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                Fecha de finalización de obra
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                {formatMonthYear(project?.end_date)}
              </Typography>
            </Box>
          </Box>
        )}

        {editMode && tab === 3 && (
          <Box sx={{ display: 'grid', gap: 3 }}>
            {(selectedLot?.lot_tickets || []).length > 0 ? (
              selectedLot.lot_tickets.map((ticket, index) => (
                <Box key={ticket.uuid || index} sx={{ display: 'grid', gap: 3 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      Datos personales
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 2,
                        mt: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Número de boleto
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {ticket.ticket_number || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Nombre completo
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {ticket.full_name || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          DNI
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {ticket.dni || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Fecha de nacimiento
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {formatDate(ticket.birth_date)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Género
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {getGenderLabel(ticket.gender)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Estado civil
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {getMaritalStatusName(ticket)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Nacionalidad
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {getNationalityName(ticket)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      Contacto
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 2,
                        mt: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Dirección
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {ticket.address || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Email
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {ticket.email || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Teléfono
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {ticket.phone_number || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Código postal
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {ticket.postal_code || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      Ubicación
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 2,
                        mt: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Distrito
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {getDistrictName(ticket)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      Otros
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 2,
                        mt: 1,
                      }}
                    >
                      <Box sx={{ gridColumn: '1 / -1' }}>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Detalles
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {ticket.details || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Creado
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {formatDateTime(ticket.created_at)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
                          Actualizado
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                          {formatDateTime(ticket.updated_at)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="body1" color="text.secondary">
                Sin boletos asociados.
              </Typography>
            )}
          </Box>
        )}
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSave}
        saveButtonProps={{ disabled: !canEditLots }}
      />
    </DialogContainer>
  );
};

export default LotDialog;
